import { inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpInterceptorFn,
} from '@angular/common/http';
import { Observable, combineLatest, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { LoginService } from 'src/app/feature/login/services/login.service';
import { OnboardingService } from 'src/app/feature/onboarding/services/onboarding.service';
import { SessionService } from 'src/app/feature/home/services/session.service';
import { TradingService } from 'src/app/feature/trading/services/trading.service';
import { SessionTimeoutService } from 'src/app/feature/login/services/session-timeout.service';
import { UserValidationStateStore } from 'src/app/shared/state/user-validation-state.store';

export const crypInterceptor: HttpInterceptorFn = (request, next) => {
  const router = inject(Router);
  const loginService = inject(LoginService);
  const onboardingService = inject(OnboardingService);
  const sessionService = inject(SessionService);
  const tradingService = inject(TradingService);
  const sessionTimeoutService = inject(SessionTimeoutService);
  const userValidationStateStore = inject(UserValidationStateStore);

  const resolveAuthorizationToken = (url: string): Observable<string | null> => {
    if (
      url.includes('user/wallets') ||
      url.includes('user/list-accounts') ||
      url.includes('user/create-accounts') ||
      url.includes('user/delete-accounts') ||
      url.includes('user/nickname') ||
      url.includes('user/alias') ||
      url.includes('user/validate-accounts') ||
      url.includes('user/update-accounts') ||
      url.includes('user/portfolio') ||
      url.includes('user/notifications') ||
      url.includes('user/quiz/') ||
      url.includes('user/validation-status') ||
      url.includes('user/coupon/') ||
      url.includes('auth/sign-out') ||
      url.includes('user/deactivate') ||
      url.includes('user/addresses') ||
      url.includes('user/coins/') ||
      url.includes('operations/onchain') ||
      url.includes('operations/transfer') ||
      url.includes('withdrawals/') ||
      url.includes('operations/quotation') ||
      url.includes('operations/exchange') ||
      url.includes('operations/confirmation') ||
      url.includes('operations/send-otp') ||
      url.includes('operations/valid-otp') ||
      url.includes('operations/history') ||
      (url.includes('operations/') && url.includes('/cancel'))
    ) {
      return sessionService.getSessionToken().pipe(take(1));
    }

    if (url.includes('auth/refresh-token')) {
      return sessionService.getSessionRefreshToken().pipe(take(1));
    }

    if (url.includes('auth/') || url.includes('enroll/')) {
      return loginService.getLoginToken().pipe(take(1));
    }

    // Registro onboarding v2 es anónimo: no enviar JWT legacy (evita 401 con token
    // previo de otro flujo / sesión larga en la misma pestaña).
    if (url.includes('onboarding/v2/register')) {
      return of(null);
    }

    if (url.includes('onboarding/')) {
      return onboardingService.getOnbToken().pipe(take(1));
    }

    return of(null);
  };

  const resolveOperationToken = (url: string): Observable<string | null> => {
    if (
      url.includes('operations/exchange') ||
      url.includes('operations/confirmation') ||
      url.includes('operations/send-otp') ||
      url.includes('operations/valid-otp')
    ) {
      return tradingService.getOperationToken().pipe(take(1));
    }

    return of(null);
  };

  return combineLatest([
    resolveAuthorizationToken(request.url),
    resolveOperationToken(request.url),
  ]).pipe(
    map(([token, operationToken]) => {
      const setHeaders: Record<string, string> = {};

      if (token) {
        setHeaders['Authorization'] = token;
      }

      if (operationToken) {
        setHeaders['Token-Operation'] = operationToken;
      }

      return Object.keys(setHeaders).length > 0
        ? request.clone({ setHeaders })
        : request;
    }),
    switchMap((requestWithHeaders) => next(requestWithHeaders)),
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && router.url !== '/') {
        sessionService.setSessionToken(null);
        loginService.setLoginToken(null);
        onboardingService.setOnbToken(null);
        tradingService.setOperationToken(null);
        sessionTimeoutService.stopCountdown();
        userValidationStateStore.clear();
        router.navigate(['/'], { replaceUrl: true });
      }
      return throwError(() => ({ ...error, appUrl: router.url }));
    }),
  );
};
